import React, { useState, useRef } from 'react';
import {
  FaFacebookF, FaWhatsapp, FaLinkedinIn, FaInstagram, FaCheck, FaAngleDown
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import { APP_CONFIG } from '../config';

const { service, contact_template, publicKey } = APP_CONFIG.emailJs;

export default function Contact() {
  const formRef = useRef();
  const [submitted, setSubmitted] = useState(false); // 'success' | null
  const [submitError, setSubmitError] = useState(null); // error message string | null
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    document.title ="OrientalPay | Contact Us";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Connect with our payroll specialists today. Book a free demo, ask about compliance, or get support with CPP, EI, T4, and ROE for your Canadian business.');
    }
  }, []);

  const validateForm = (data) => {
    return (
      data.get('name')?.trim() &&
      data.get('email')?.trim() &&
      data.get('service')?.trim() &&
      data.get('message')?.trim()
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    setLoading(true);

    // Secondary validation (belt-and-suspenders alongside HTML5 `required`)
    const data = new FormData(formRef.current);
    if (!validateForm(data)) {
      setSubmitError('Please fill in all required fields before sending.');
      setLoading(false);
      return;
    }

    try {
      await emailjs.sendForm(service, contact_template, formRef.current, publicKey);
      setSubmitted(true);
      formRef.current.reset();
    } catch (error) {
      console.error('EmailJS error:', error);
      setSubmitError(error?.text || error?.message || 'Unable to send your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      
      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img
          src="/contect.png"
          alt="Contact Oriental Business Solutions"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1e45] via-[#0d1e45]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d1e45]/80 via-transparent to-transparent" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-300 text-xs font-bold uppercase tracking-widest mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.05] mb-6 max-w-3xl">
            Let's Simplify Your<br />
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #ffbe1a, #ffd966)' }}>
              Payroll Together
            </span>
          </h1>
          <p className="text-blue-100 text-base md:text-lg font-medium leading-relaxed max-w-xl">
            Our experts are ready to help you get started, answer questions, or set up a live demo.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            
            {/* Left Column - Contact Info */}
            <div className="md:col-span-6 lg:col-span-7 flex flex-col pt-4">
              <span className="text-[#3b5eb4] text-xs font-bold uppercase tracking-widest mb-1">
                CONTACT
              </span>
              <h1 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
                Get In Touch
              </h1>
              <p className="text-gray-600 text-sm leading-relaxed mb-8 max-w-xl font-medium">
                Connect with us today for expert payroll compliance and management. Our dedicated team is here to provide personalized solutions that ensure your pay runs are on-time, accurate, and fully compliant with all CRA guidelines.
              </p>

              <div className="grid sm:grid-cols-2 gap-8 mb-8">
                {/* Business Hours */}
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 mb-3">
                    Business Hours
                  </h3>
                  <ul className="space-y-2 text-xs font-semibold text-gray-500">
                    <li>Monday - Thursday: 9:30am - 8:00pm</li>
                    <li>Friday: 9:00am - 9:30pm</li>
                    <li>Saturday: 8:30am - 10:30pm</li>
                    <li>Sunday: 9:00am - 12:00pm</li>
                  </ul>
                </div>

                {/* Contact Info */}
                <div>
                  <h3 className="text-lg font-extrabold text-gray-900 mb-3">
                    Contact
                  </h3>
                  <ul className="space-y-2 text-xs font-semibold text-gray-500">
                    
                    <li>
                      <a href={APP_CONFIG.emailLink} className="hover:text-[#3b5eb4] transition-colors">
                        {APP_CONFIG.email}
                      </a>
                    </li>
                    <li>
                      <a href={APP_CONFIG.phoneLink} className="hover:text-[#3b5eb4] transition-colors">
                        {APP_CONFIG.phone}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Follow Us */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-lg font-extrabold text-gray-900 mb-1">
                  Follow Us
                </h3>
                <p className="text-xs font-semibold text-gray-400 mb-3">
                  Stay connected with us on social media
                </p>
                <div className="flex gap-4 text-lg">
                  <a href={APP_CONFIG.facebookLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-[#3b5eb4] hover:bg-gray-100 transition-colors">
                    <FaFacebookF />
                  </a>
                  <a href={APP_CONFIG.whatsAppLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-[#3b5eb4] hover:bg-gray-100 transition-colors">
                    <FaWhatsapp />
                  </a>
                  <a href={APP_CONFIG.linkedInLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-[#3b5eb4] hover:bg-gray-100 transition-colors">
                    <FaLinkedinIn />
                  </a>
                  <a href={APP_CONFIG.instaLink} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-[#3b5eb4] hover:bg-gray-100 transition-colors">
                    <FaInstagram />
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column - Form Card */}
            <div className="md:col-span-6 lg:col-span-5">
              {submitted ? (
                <div className="bg-[#3b5eb4] rounded-3xl p-8 text-white text-center shadow-2xl flex flex-col items-center justify-center min-h-[460px] border border-blue-400/20">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg mb-6">
                    <FaCheck className="text-[#3b5eb4] text-3xl animate-bounce" />
                  </div>
                  <h2 className="text-3xl font-black mb-3">Message Sent!</h2>
                  <p className="text-blue-100 text-sm mb-8 max-w-xs font-medium leading-relaxed">
                    Your message has been sent successfully. We will get back to you shortly.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                    }}
                    className="px-8 py-3.5 bg-[#ffbe1a] hover:bg-[#ffcd53] text-gray-900 text-sm font-bold rounded-xl transition-all shadow-md"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form
                  ref={formRef}
                  onSubmit={handleSubmit}
                  className="bg-[#1a3268] rounded-3xl p-8 text-white shadow-2xl flex flex-col border border-blue-400/20"
                >
                  <h2 className="text-3xl font-black mb-2">Send A Message</h2>
                  <p className="text-blue-100 text-xs font-medium mb-6 leading-relaxed opacity-90">
                    Use our contact form below to book a free demo, ask questions, or request onboarding support.
                  </p>
                  
                  {submitError && (
                    <div className="bg-red-100 border border-red-300 text-red-800 text-sm font-medium px-4 py-2.5 rounded-lg mb-4">
                      {submitError}
                    </div>
                  )}

                  <div className="flex flex-col gap-4">
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Name"
                      className="w-full bg-white rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffbe1a] font-medium"
                    />

                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="Email"
                      className="w-full bg-white rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffbe1a] font-medium"
                    />

                    <div className="relative">
                      <select
                        name="service"
                        defaultValue=""
                        className="w-full bg-white rounded-xl px-4 py-3.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#ffbe1a] appearance-none font-medium"
                      >
                        <option value="">Select a service</option>
                        <option value="Tax Consulting">Tax Consulting</option>
                        <option value="Bookkeeping Services">Bookkeeping Services</option>
                        <option value="Taxes">Taxes</option>
                        <option value="Business Registration">Business Registration</option>
                        <option value="Loans & Mortgages">Loans & Mortgages</option>
                        <option value="Rental Property HST Rebates">Rental Property HST Rebates</option>
                        <option value="Auditing">Auditing</option>
                        <option value="Payroll Management">Payroll Management</option>
                        <option value="Tax Planning and Reporting">Tax Planning and Reporting</option>
                        <option value="Legally Required">Legally Required</option>
                        <option value="OrientalPay - Payroll">OrientalPay - Payroll</option>
                        <option value="CRA fax service">CRA fax service</option>
                      </select>
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-500">
                        <FaAngleDown />
                      </div>
                    </div>

                    <textarea
                      name="message"
                      required
                      rows={4}
                      placeholder="Message"
                      className="w-full bg-white rounded-xl px-4 py-3.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffbe1a] resize-none font-medium"
                    />

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 mt-2 bg-[#ffbe1a] hover:bg-[#ffcd53] text-gray-900 text-sm font-extrabold rounded-xl transition-all shadow-lg active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        'Send Message'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
            
          </div>
        </div>
      </section>
    </div>
  );
}
